import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Phone,
  Mail,
  Globe,
  MapPin,
  Building2,
  Download,
  ExternalLink,
  User,
} from 'lucide-react';
import { VCardData, generateVCard, downloadVCard, validateVCard } from '@/lib/vcard-utils';

interface VCardPreviewProps {
  data: VCardData;
}

export function VCardPreview({ data }: VCardPreviewProps) {
  const validation = validateVCard(data);
  
  const fullName = [data.prefix, data.firstName, data.middleName, data.lastName, data.suffix]
    .filter(Boolean)
    .join(' ');

  const initials = `${data.firstName?.charAt(0) || ''}${data.lastName?.charAt(0) || ''}`.toUpperCase();

  const primaryPhone = data.phoneNumbers.find((p) => p.number);
  const primaryEmail = data.emails.find((e) => e.email);
  const primaryWebsite = data.websites.find((w) => w.url);
  const primaryAddress = data.addresses.find((a) => a.city || a.street1);

  const handleDownload = () => {
    downloadVCard(data);
  };

  const handleTest = () => {
    const vcardContent = generateVCard(data);
    const blob = new Blob([vcardContent], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    
    // Try to open vCard directly (works on mobile)
    window.open(url, '_blank');
    
    // Clean up after a delay
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="space-y-4">
      {/* iOS-style Contact Card Preview */}
      <div className="bg-card border rounded-xl overflow-hidden shadow-sm">
        {/* Header */}
        <div className="bg-muted/30 p-6 text-center">
          <Avatar className="h-24 w-24 mx-auto mb-3 border-4 border-background shadow-lg">
            <AvatarImage src={data.photoUrl} alt={fullName} />
            <AvatarFallback className="text-2xl bg-primary/10 text-primary">
              {initials || <User className="h-10 w-10" />}
            </AvatarFallback>
          </Avatar>
          
          <h3 className="text-xl font-semibold">
            {fullName || 'Your Name'}
          </h3>
          
          {(data.jobTitle || data.company) && (
            <p className="text-sm text-muted-foreground mt-1">
              {[data.jobTitle, data.company].filter(Boolean).join(' at ')}
            </p>
          )}
        </div>

        {/* Contact Info */}
        <div className="p-4 space-y-1">
          {primaryPhone && (
            <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Phone className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground capitalize">{primaryPhone.type}</p>
                <p className="text-sm font-medium truncate">
                  {primaryPhone.number || 'No number'}
                </p>
              </div>
            </div>
          )}

          {primaryEmail && (
            <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground capitalize">{primaryEmail.type}</p>
                <p className="text-sm font-medium truncate">
                  {primaryEmail.email || 'No email'}
                </p>
              </div>
            </div>
          )}

          {primaryWebsite && (
            <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Globe className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground capitalize">{primaryWebsite.type}</p>
                <p className="text-sm font-medium truncate">
                  {primaryWebsite.url?.replace(/^https?:\/\//, '') || 'No website'}
                </p>
              </div>
            </div>
          )}

          {data.company && (
            <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Building2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">Company</p>
                <p className="text-sm font-medium truncate">
                  {data.company}
                  {data.department && ` · ${data.department}`}
                </p>
              </div>
            </div>
          )}

          {primaryAddress && (
            <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <MapPin className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground capitalize">{primaryAddress.type}</p>
                <p className="text-sm font-medium">
                  {[primaryAddress.city, primaryAddress.state, primaryAddress.country]
                    .filter(Boolean)
                    .join(', ') || 'No address'}
                </p>
              </div>
            </div>
          )}

          {!primaryPhone && !primaryEmail && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              Add contact details to see preview
            </div>
          )}
        </div>

        {/* Stats */}
        {!data.minimalVCard && (
          <>
            <Separator />
            <div className="px-4 py-3 flex justify-around text-center text-xs text-muted-foreground">
              <div>
                <p className="font-medium text-foreground">{data.phoneNumbers.filter(p => p.number).length}</p>
                <p>Phones</p>
              </div>
              <div>
                <p className="font-medium text-foreground">{data.emails.filter(e => e.email).length}</p>
                <p>Emails</p>
              </div>
              <div>
                <p className="font-medium text-foreground">{data.addresses.length}</p>
                <p>Addresses</p>
              </div>
              <div>
                <p className="font-medium text-foreground">{data.websites.length}</p>
                <p>Websites</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Validation Errors */}
      {!validation.isValid && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm font-medium text-destructive mb-1">Please fix the following:</p>
          <ul className="text-xs text-destructive/80 list-disc list-inside space-y-0.5">
            {validation.errors.map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleDownload}
          disabled={!validation.isValid}
          className="flex-1"
        >
          <Download className="h-4 w-4 mr-2" />
          Download .vcf
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleTest}
          disabled={!validation.isValid}
          className="flex-1"
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Test vCard
        </Button>
      </div>

      {/* vCard Info */}
      <div className="text-xs text-muted-foreground text-center">
        vCard {data.vcardVersion} • {data.minimalVCard ? 'Minimal' : 'Detailed'} format
      </div>
    </div>
  );
}
